import React from 'react';
import { GraduationCap, BookOpen, Car, CheckCircle, ShieldCheck, Clock, MapPin } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

const InstructorGuide = () => {
    const handleRegister = (type) => {
        // We will pass this down or use context/navigate if needed, 
        // but since this page mimics App structure, we might need the modal here too 
        // or just link back to home with a query param.
        // For simplicity, we will assume standard navigation or add modal later.
        window.location.href = '/?register=instructor';
    };

    return (
        <div className="guide-page">
            <Navbar />

            <div className="guide-hero">
                <div className="container">
                    <h1>Como ser Instrutor de Trânsito</h1>
                    <p>O guia definitivo atualizado para 2024/2025.</p>
                </div>
            </div>

            <div className="container guide-content">
                <section className="intro-section glass-panel">
                    <h2>Uma Nova Era para Instrutores</h2>
                    <p>
                        Com a Lei 12.302/2010 e as novas resoluções do CONTRAN para 2025,
                        o mercado de instrução de trânsito está mudando. A figura do
                        <strong> Instrutor Autônomo</strong> ganha força, permitindo maior liberdade
                        e potencial de ganhos.
                    </p>
                </section>

                <section className="requirements-section">
                    <h3><ShieldCheck className="icon" /> Requisitos Básicos</h3>
                    <div className="req-grid">
                        <div className="req-card">
                            <span className="req-number">21</span>
                            <h4>Anos de Idade</h4>
                            <p>Idade mínima exigida por lei.</p>
                        </div>
                        <div className="req-card">
                            <span className="req-number">EM</span>
                            <h4>Ensino Médio</h4>
                            <p>Comprovante de escolaridade completo.</p>
                        </div>
                        <div className="req-card">
                            <span className="req-number">2+</span>
                            <h4>Anos de CNH</h4>
                            <p>Habilitado na categoria há pelo menos 2 anos.</p>
                        </div>
                        <div className="req-card">
                            <span className="req-number">OK</span>
                            <h4>Sem Infrações</h4>
                            <p>Nenhuma infração gravíssima nos últimos 60 dias.</p>
                        </div>
                    </div>
                </section>

                <section className="timeline-section">
                    <h3>Passo a Passo da Formação</h3>
                    <div className="timeline">
                        <div className="timeline-item">
                            <div className="timeline-marker">1</div>
                            <div className="timeline-content">
                                <h4>Curso de Capacitação (180h)</h4>
                                <p>Realizar curso homologado pelo Detran, abordando legislação, direção defensiva, primeiros socorros, mecânica e didática.</p>
                            </div>
                        </div>
                        <div className="timeline-item">
                            <div className="timeline-marker">2</div>
                            <div className="timeline-content">
                                <h4>Exame Psicológico</h4>
                                <p>Avaliação específica para fins pedagógicos, obrigatória para obter a credencial.</p>
                            </div>
                        </div>
                        <div className="timeline-item">
                            <div className="timeline-marker">3</div>
                            <div className="timeline-content">
                                <h4>Prova Teórica</h4>
                                <p>Aprovação no exame do Detran comprovando conhecimentos técnicos e didáticos.</p>
                            </div>
                        </div>
                        <div className="timeline-item">
                            <div className="timeline-marker">4</div>
                            <div className="timeline-content">
                                <h4>Credenciamento</h4>
                                <p>Solicitação da credencial junto ao Detran do seu estado (validade de 5 anos).</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="vehicle-section glass-panel">
                    <h3><Car className="icon" /> O Veículo de Instrução</h3>
                    <ul>
                        <li><CheckCircle size={18} /> Veículo com duplo comando de freio e embreagem.</li>
                        <li><CheckCircle size={18} /> Identificação "AUTOESCOLA" ou "INSTRUÇÃO".</li>
                        <li><CheckCircle size={18} /> Idade máxima: 8 anos (motos) a 12 anos (carros)*.</li>
                        <li><CheckCircle size={18} /> Vistoria e licenciamento especial.</li>
                    </ul>
                    <small>*Pode variar conforme estado.</small>
                </section>

                <section className="cta-section">
                    <h2>Pronto para começar?</h2>
                    <p>Cadastre-se no AutoMestre para ser avisado quando a plataforma de alunos abrir.</p>
                    <button onClick={handleRegister} className="btn-primary large">
                        Quero ser Instrutor Parceiro
                    </button>
                </section>
            </div>

            <Footer />
            <style>{`
                .guide-page {
                    padding-top: 80px;
                    background: var(--bg-dark);
                    color: var(--text-light);
                }
                .guide-hero {
                    background: linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=80');
                    background-size: cover;
                    background-position: center;
                    padding: 8rem 0 6rem;
                    text-align: center;
                    margin-bottom: 4rem;
                }
                .guide-hero h1 {
                    font-size: 3.5rem;
                    margin-bottom: 1rem;
                    color: #fff;
                }
                .guide-content {
                    max-width: 900px;
                }
                .intro-section {
                    padding: 2.5rem;
                    margin-bottom: 4rem;
                    border: 1px solid var(--primary);
                    background: rgba(29, 78, 216, 0.05);
                }
                .intro-section h2 { margin-bottom: 1rem; color: var(--text-light); }
                .intro-section p { font-size: 1.1rem; line-height: 1.6; color: var(--text-gray); }

                .requirements-section h3, .timeline-section h3, .vehicle-section h3 {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 2rem;
                    color: var(--text-light);
                    font-size: 1.8rem;
                }
                
                .req-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1.5rem;
                    margin-bottom: 4rem;
                }
                .req-card {
                    background: rgba(255,255,255,0.03);
                    padding: 1.5rem;
                    border-radius: 12px;
                    text-align: center;
                    border: 1px solid var(--glass-border);
                }
                .req-number {
                    display: block;
                    font-size: 2.5rem;
                    font-weight: 800;
                    color: var(--primary);
                    margin-bottom: 0.5rem;
                }
                .req-card h4 { margin-bottom: 0.5rem; color: #fff; }
                .req-card p { font-size: 0.9rem; color: var(--text-gray); }

                .timeline {
                    position: relative;
                    margin-bottom: 4rem;
                    padding-left: 2rem;
                }
                .timeline::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    width: 2px;
                    background: var(--glass-border);
                }
                .timeline-item {
                    position: relative;
                    margin-bottom: 2.5rem;
                }
                .timeline-marker {
                    position: absolute;
                    left: -2.9rem;
                    width: 2rem;
                    height: 2rem;
                    background: var(--primary);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    color: #fff;
                    box-shadow: 0 0 10px rgba(29,78,216,0.5);
                }
                .timeline-content h4 { color: #fff; margin-bottom: 0.5rem; font-size: 1.2rem; }
                .timeline-content p { color: var(--text-gray); }

                .vehicle-section {
                    padding: 2.5rem;
                    margin-bottom: 4rem;
                }
                .vehicle-section ul { list-style: none; padding: 0; }
                .vehicle-section li {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-bottom: 1rem;
                    color: var(--text-gray);
                    font-size: 1.1rem;
                }
                .vehicle-section li svg { color: var(--primary); min-width: 20px; }

                .cta-section {
                    text-align: center;
                    padding: 4rem 2rem;
                    background: linear-gradient(to top, rgba(29,78,216,0.1), transparent);
                    border-radius: 20px;
                    margin-bottom: 4rem;
                }
                .cta-section h2 { font-size: 2.5rem; margin-bottom: 1rem; color: #fff; }
                .cta-section p { margin-bottom: 2rem; color: var(--text-gray); font-size: 1.2rem; }
                
                @media(max-width: 768px) {
                    .guide-hero h1 { font-size: 2.5rem; }
                    .req-grid { grid-template-columns: 1fr 1fr; }
                }
            `}</style>
        </div>
    );
};

export default InstructorGuide;
